import { alpha, InputBase, Typography, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { css } from '@emotion/react';
import { KeyboardEvent, ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { ScheduleFrequency, SourcingList, SourcingSavedSearch } from '../../schemas/SourcingList.schema';
import { FMT } from '../../util/formatter-service';
import {
  calculateNextRun,
  formatNextRun as formatNextRunDate,
  FREQUENCIES,
  hydrateSchedule,
} from './schedule/schedule';
import { sourcingTitleEditingAtom } from './state/SourcingListsState';

// ── Styles ─────────────────────────────────────────────────────────────────────

const headerCss = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
  gap: 1rem;
`;

const titleSectionCss = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const subtextRowCss = css`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  height: 28px;
`;

const actionsCss = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const titleInputSx: SxProps<Theme> = {
  fontSize: '1.25rem',
  fontWeight: 400,
  lineHeight: 1.6,
  letterSpacing: '0.15px',
  color: 'text.primary',
  width: '90%',
  '& input': { padding: 0, borderBottom: 1, borderColor: 'divider' },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(value: string | null, fallback: string) {
  return (value && FMT.format('dateLocal', value)) || fallback;
}

function frequencyLabel(frequency: ScheduleFrequency | null | undefined): string {
  return FREQUENCIES.find((f) => f.value === frequency)?.label ?? 'Not Scheduled';
}

function useNextRunLabel(list: SourcingList | SourcingSavedSearch): string {
  return useMemo(() => {
    const schedule = hydrateSchedule(list);
    if (!schedule) {
      return '—';
    }
    const next = calculateNextRun(schedule);
    if (!next) {
      return '—';
    }
    return formatNextRunDate(next);
  }, [list]);
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface TitleFieldProps {
  readonly currentName: string;
  readonly onSave: (newName: string) => void;
}

function TitleField({ currentName, onSave }: TitleFieldProps) {
  const [editValue, setEditValue] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  const [{ isEditing, initialValue }, setTitleEditing] = useAtom(sourcingTitleEditingAtom);

  useEffect(() => {
    if (isEditing) {
      setEditValue(initialValue);
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }
  }, [isEditing, initialValue]);

  const stopEditing = () => setTitleEditing({ isEditing: false, initialValue: '' });

  const commitEdit = () => {
    stopEditing();
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === currentName) {
      return;
    }
    onSave(trimmed);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitEdit();
    }
    if (e.key === 'Escape') {
      stopEditing();
    }
  };

  if (isEditing) {
    return (
      <InputBase
        inputRef={titleInputRef as RefObject<HTMLInputElement>}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        sx={titleInputSx}
      />
    );
  }

  return (
    <Typography variant='h6' noWrap sx={{ letterSpacing: 0.15, lineHeight: 1.6 }}>
      {currentName}
    </Typography>
  );
}

function Subtext({ list }: { readonly list: SourcingList | SourcingSavedSearch }) {
  const { colors } = useTheme();
  const captionColor = alpha(colors.neutral[70], 0.64);
  const scheduleFrequency =
    list.listType === 'active' ? list.refreshScheduleFrequency : list.enrichScheduleFrequency;
  const nextRun = useNextRunLabel(list);
  const scheduleSegment = scheduleFrequency
    ? `${list.listType === 'active' ? 'Refresh' : 'Auto-Enrich'}: ${frequencyLabel(scheduleFrequency)}. Next Run: ${nextRun}`
    : 'Scheduled: Not Set';

  if (list.listType === 'active') {
    return (
      <>
        <Typography
          variant='caption'
          sx={{
            backgroundColor: colors.primary[5],
            color: alpha(colors.primary[100], 0.87),
            borderRadius: '0.125rem',
            padding: '0.25rem 0.5rem',
            lineHeight: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Query: {list.query ?? '—'}
        </Typography>
        <Typography variant='caption' sx={{ color: captionColor, lineHeight: 'normal' }}>
          {scheduleSegment}
        </Typography>
      </>
    );
  }

  const enriched = formatDate(list.lastEnrichDate ?? null, 'Never');

  return (
    <Typography variant='caption' sx={{ color: captionColor, lineHeight: 'normal' }}>
      Enriched: {enriched}. {scheduleSegment}
    </Typography>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface SourcingHeaderProps {
  readonly selectedList: SourcingList | SourcingSavedSearch | null;
  readonly onSaveTitle: (newName: string) => void;
  readonly children: ReactNode;
}

export function SourcingHeader({ selectedList, onSaveTitle, children }: SourcingHeaderProps) {
  return (
    <div css={headerCss}>
      <div css={titleSectionCss}>
        <TitleField currentName={selectedList?.name ?? '—'} onSave={onSaveTitle} />

        {selectedList && (
          <div css={subtextRowCss}>
            <Subtext list={selectedList} />
          </div>
        )}
      </div>

      <div css={actionsCss}>{children}</div>
    </div>
  );
}
